import { useState, useMemo, useEffect } from 'react'
import {
    ChevronLeft, ChevronRight, CalendarPlus, ListChecks,
    Clock, Calendar, Users, CheckSquare, Bell, Flag, CalendarDays,
    Trash2, Plus, X, ChevronDown, ChevronUp,
} from 'lucide-react'

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DAYS_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const DAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const COLORS = ['#4F46E5', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#A855F7']

const CAT_ICONS = {
    meeting: <Users size={11} />,
    task: <CheckSquare size={11} />,
    reminder: <Bell size={11} />,
    event: <CalendarDays size={11} />,
    deadline: <Flag size={11} />,
}

function fmt(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function fmtDisplay(dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
}

const SEED_EVENTS = (() => {
    const t = new Date(), y = t.getFullYear()
    const m = String(t.getMonth() + 1).padStart(2, '0')
    const d = n => String(Math.min(t.getDate() + n, 28)).padStart(2, '0')
    return [
        { id: 1, name: 'Team Standup', date: `${y}-${m}-${d(0)}`, time: '09:00', cat: 'meeting', color: '#4F46E5', note: 'Daily sync' },
        { id: 2, name: 'Design Review', date: `${y}-${m}-${d(2)}`, time: '14:00', cat: 'event', color: '#A855F7', note: 'Q3 design system review' },
        { id: 3, name: 'Submit Report', date: `${y}-${m}-${d(5)}`, time: '17:00', cat: 'deadline', color: '#EF4444', note: '' },
        { id: 4, name: '1-on-1 with Manager', date: `${y}-${m}-${d(1)}`, time: '11:00', cat: 'meeting', color: '#06B6D4', note: '' },
    ]
})()

const CSS = `
.cp-wrap {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 20px;
  padding: 0 ;
  align-items: start;
}
.cp-top {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 0 20px 0; flex-wrap: wrap; gap: 10px;
}
.cp-right { display: flex; flex-direction: column; gap: 16px; }
.cp-two   { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.cp-dow   { display: grid; grid-template-columns: repeat(7,1fr); margin-bottom: 4px; }
.cp-days  { display: grid; grid-template-columns: repeat(7,1fr); gap: 2px; }

.cp-vtabs { display:flex; gap:3px; padding:4px; background:var(--bg-surface); border-radius:var(--radius-md); border:1px solid rgba(255,255,255,0.05); }
.cp-vtab  { font-size:11px; font-weight:500; padding:5px 13px; background:transparent; border:none; color:var(--text3); border-radius:var(--radius-sm); cursor:pointer; transition:all .15s; white-space:nowrap; }
.cp-vtab:hover  { color:var(--text2); background:var(--bg-hover); }
.cp-vtab.active { background:var(--bg-raised); color:var(--text1); box-shadow:0 1px 3px rgba(0,0,0,.4); }

.cp-day {
  border-radius: var(--radius-sm);
  display: flex; flex-direction: column; align-items: center; justify-content: flex-start;
  padding: 3px 2px; cursor: pointer; transition: background .12s;
  min-height: 100px; border: 1px solid transparent;
}
.cp-day:hover { background: var(--bg-hover); }
.cp-day.sel   { background: var(--bg-hover); border-color: var(--border-active); }
.cp-day-num   { font-size:12px; font-weight:500; width:24px; height:24px; display:flex; align-items:center; justify-content:center; flex-shrink:0; color:var(--text2); border-radius:0; }
.cp-day.today .cp-day-num { background:var(--accent); color:#fff; border-radius:50%; }
.cp-day.other .cp-day-num { color:var(--text4); }

.cp-ev {
  background: var(--bg-raised); border: 1px solid var(--border);
  border-radius: var(--radius-sm); padding: 10px 12px; transition: background .12s; cursor: default;
}
.cp-ev:hover { background: var(--bg-hover); }

.cp-form-card {
  background: var(--bg-card); border: 1px solid var(--border);
  border-top: 2px solid var(--accent); border-radius: var(--radius-lg);
  padding: 16px; backdrop-filter: blur(16px);
}
.cp-ev-card {
  background: var(--bg-card); border: 1px solid var(--border);
  border-radius: var(--radius-lg); overflow: hidden; backdrop-filter: blur(16px);
}
.cp-cal-card {
  background: var(--bg-card); border: 1px solid var(--border);
  border-radius: var(--radius-lg); overflow: hidden; backdrop-filter: blur(16px);
}

.cp-field       { display:flex; flex-direction:column; gap:5px; margin-bottom:10px; }
.cp-field label { font-size:10px; font-weight:600; color:var(--text3); text-transform:uppercase; letter-spacing:.08em; }
.cp-inp {
  background: var(--bg-raised); border: 1px solid var(--border); border-radius: var(--radius-sm);
  color: var(--text1); font-family: var(--font-body); font-size: 13px;
  padding: 7px 10px; outline: none; width: 100%; transition: border-color .15s, box-shadow .15s;
}
.cp-inp:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.10); }
.cp-inp.err   { border-color: var(--color-red); }
.cp-inp::placeholder { color: var(--text3); }
.cp-inp option { background: var(--bg-raised); }

.cp-add-btn {
  width:100%; background:var(--accent); color:#fff; border:none;
  border-radius:var(--radius-sm); padding:9px; font-family:var(--font-body);
  font-size:12px; font-weight:600; cursor:pointer; display:flex;
  align-items:center; justify-content:center; gap:6px; transition:all .15s; margin-top:4px;
}
.cp-add-btn:hover { background:var(--accent-hover); transform:translateY(-1px); }

/* ── MOBILE MODAL OVERLAY ── */
.cp-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: cp-fade-in 0.2s ease;
}
@keyframes cp-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
.cp-modal-sheet {
  width: 100%;
  max-width: 520px;
  background: var(--bg-card, #1e1e2e);
  border-radius: 20px 20px 0 0;
  padding: 0 0 24px;
  max-height: 90vh;
  overflow-y: auto;
  animation: cp-slide-up 0.25s cubic-bezier(0.32,0.72,0,1);
}
@keyframes cp-slide-up {
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
}
.cp-modal-handle {
  width: 40px; height: 4px;
  background: var(--border, rgba(255,255,255,0.15));
  border-radius: 2px;
  margin: 12px auto 0;
}
.cp-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px 12px;
  border-bottom: 1px solid var(--border);
}
.cp-modal-body {
  padding: 16px;
}

.cp-mob-add-toggle {
  display: none;
  width: 100%; background: var(--accent); color: #fff; border: none;
  border-radius: var(--radius-md); padding: 11px 16px; font-family: var(--font-body);
  font-size: 13px; font-weight: 600; cursor: pointer;
  align-items: center; justify-content: center; gap: 8px; transition: all .15s;
}
.cp-mob-add-toggle:hover { background: var(--accent-hover); }

@media (max-width: 768px) {
  .cp-wrap {
    grid-template-columns: 1fr;
    padding: 0 14px 32px;
    gap: 14px;
  }
  .cp-top {
    padding: 14px 14px 12px;
    gap: 8px;
  }
    .cp-day { min-height: 36px; }
  .cp-right-desktop { display: none !important; }
  .cp-mob-section   { display: flex !important; }
  .cp-mob-add-toggle { display: flex !important; }
  .cp-two { grid-template-columns: 1fr 1fr; }
}
.cp-mob-section { display: none; flex-direction: column; gap: 14px; }

@media (max-width: 420px) {
  .cp-wrap { padding: 0 10px 28px; gap: 12px; }
  .cp-top  { padding: 12px 10px 10px; }
  .cp-two  { grid-template-columns: 1fr; }
  .cp-vtab { padding: 5px 10px; }
  .cp-day { min-height: 36px; }
}
`

function useCSS() {
    useEffect(() => {
        const id = 'cp-styles'
        if (document.getElementById(id)) return
        const el = document.createElement('style')
        el.id = id; el.textContent = CSS
        document.head.appendChild(el)
        return () => document.getElementById(id)?.remove()
    }, [])
}

function useIsMobile() {
    const [v, set] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768)
    useEffect(() => {
        const fn = () => set(window.innerWidth <= 768)
        window.addEventListener('resize', fn)
        return () => window.removeEventListener('resize', fn)
    }, [])
    return v
}

function ColorPicker({ value, onChange }) {
    return (
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {COLORS.map(c => (
                <button key={c} onClick={() => onChange(c)} style={{
                    width: 22, height: 22, borderRadius: '50%', background: c, flexShrink: 0,
                    border: value === c ? '2px solid #fff' : '2px solid transparent',
                    outline: value === c ? `2px solid ${c}` : 'none',
                    cursor: 'pointer', transform: value === c ? 'scale(1.15)' : 'scale(1)', transition: 'all .15s',
                }} />
            ))}
        </div>
    )
}

function EventItem({ ev, onDelete }) {
    return (
        <div className="cp-ev" style={{ borderLeft: `3px solid ${ev.color}` }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)', marginBottom: 4 }}>{ev.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Calendar size={10} />{ev.date}</span>
                        {ev.time && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Clock size={10} />{ev.time}</span>}
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>{CAT_ICONS[ev.cat]}{ev.cat}</span>
                    </div>
                    {ev.note && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.note}</div>}
                </div>
                <button onClick={() => onDelete(ev.id)}
                    style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', flexShrink: 0, transition: 'color .15s' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--color-red)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                ><Trash2 size={14} /></button>
            </div>
        </div>
    )
}

function AddEventForm({ form, setForm, formErr, onAdd }) {
    return (
        <>
            <div className="cp-field">
                <label>Event Name</label>
                <input className={`cp-inp${formErr && !form.name ? ' err' : ''}`} value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Team standup" />
            </div>
            <div className="cp-two">
                <div className="cp-field">
                    <label>Date</label>
                    <input type="date" className={`cp-inp${formErr && !form.date ? ' err' : ''}`} value={form.date}
                        onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
                </div>
                <div className="cp-field">
                    <label>Time</label>
                    <input type="time" className="cp-inp" value={form.time}
                        onChange={e => setForm(f => ({ ...f, time: e.target.value }))} />
                </div>
            </div>
            <div className="cp-field">
                <label>Category</label>
                <select className="cp-inp" value={form.cat} onChange={e => setForm(f => ({ ...f, cat: e.target.value }))}>
                    <option value="meeting">Meeting</option>
                    <option value="task">Task</option>
                    <option value="reminder">Reminder</option>
                    <option value="event">Event</option>
                    <option value="deadline">Deadline</option>
                </select>
            </div>
            <div className="cp-field">
                <label>Color</label>
                <ColorPicker value={form.color} onChange={c => setForm(f => ({ ...f, color: c }))} />
            </div>
            <div className="cp-field">
                <label>Note (optional)</label>
                <textarea className="cp-inp" style={{ resize: 'vertical', minHeight: 54 }} value={form.note}
                    onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Add a short description..." />
            </div>
            <button className="cp-add-btn" onClick={onAdd}><Plus size={13} /> Add Event</button>
        </>
    )
}

// ── NEW: Mobile Bottom Sheet Modal ──────────────────────────────────────────
function MobileAddModal({ form, setForm, formErr, onAdd, onClose }) {
    // Close on backdrop click
    function handleBackdrop(e) {
        if (e.target === e.currentTarget) onClose()
    }

    // Prevent body scroll when modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden'
        return () => { document.body.style.overflow = '' }
    }, [])

    return (
        <div className="cp-modal-overlay" onMouseDown={handleBackdrop}>
            <div className="cp-modal-sheet">
                <div className="cp-modal-handle" />
                <div className="cp-modal-header">
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text1)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CalendarPlus size={16} color="var(--accent)" /> Schedule Event
                    </div>
                    <button onClick={onClose} style={{
                        background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', padding: 4, borderRadius: 'var(--radius-sm)',
                    }}>
                        <X size={18} />
                    </button>
                </div>
                <div className="cp-modal-body">
                    <AddEventForm form={form} setForm={setForm} formErr={formErr} onAdd={onAdd} />
                </div>
            </div>
        </div>
    )
}

function EventsList({ events, onDelete, activeView, selectedDate }) {
    return (
        <div className="cp-ev-card">
            <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text1)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ListChecks size={14} color="var(--color-cyan)" />
                    {activeView === 'agenda' ? 'All Events' : selectedDate ? fmtDisplay(selectedDate) : 'Events'}
                </div>
                <span style={{ background: 'rgba(79,70,229,0.10)', color: 'var(--accent-hover)', border: '1px solid var(--border)', borderRadius: 'var(--radius-full)', fontSize: 10, fontWeight: 600, padding: '2px 9px' }}>
                    {events.length}
                </span>
            </div>
            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                {events.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text3)', fontSize: 12 }}>
                        <Calendar size={26} style={{ display: 'block', margin: '0 auto 8px', opacity: .35 }} />
                        {selectedDate ? 'No events on this day.' : 'No events yet.'}
                    </div>
                ) : events.map(ev => <EventItem key={ev.id} ev={ev} onDelete={onDelete} />)}
            </div>
        </div>
    )
}

export default function CalendarPage() {
    useCSS()
    const isMobile = useIsMobile()

    const todayStr = fmt(new Date())
    const [viewDate, setViewDate] = useState(() => { const t = new Date(); return { y: t.getFullYear(), m: t.getMonth() } })
    const [selectedDate, setSelectedDate] = useState(todayStr)
    const [events, setEvents] = useState(SEED_EVENTS)
    const [nextId, setNextId] = useState(100)
    const [activeView, setActiveView] = useState('month')
    // ── showAddForm now controls the MODAL on mobile ──
    const [showAddForm, setShowAddForm] = useState(false)

    const [form, setForm] = useState({ name: '', date: todayStr, time: '', cat: 'meeting', color: '#4F46E5', note: '' })
    const [formErr, setFormErr] = useState(false)

    const calCells = useMemo(() => {
        const { y, m } = viewDate
        const first = new Date(y, m, 1).getDay()
        const dim = new Date(y, m + 1, 0).getDate()
        const dip = new Date(y, m, 0).getDate()
        const cells = []
        for (let i = first - 1; i >= 0; i--)
            cells.push({ day: dip - i, month: m - 1 < 0 ? 11 : m - 1, year: m === 0 ? y - 1 : y, cur: false })
        for (let d = 1; d <= dim; d++)
            cells.push({ day: d, month: m, year: y, cur: true })
        const rem = 42 - cells.length
        for (let d = 1; d <= rem; d++)
            cells.push({ day: d, month: m + 1 > 11 ? 0 : m + 1, year: m === 11 ? y + 1 : y, cur: false })
        return cells
    }, [viewDate])

    const evsByDate = useMemo(() => {
        const map = {}
        events.forEach(ev => { (map[ev.date] = map[ev.date] || []).push(ev) })
        return map
    }, [events])

    const filteredEvents = useMemo(() =>
        events
            .filter(ev => activeView === 'agenda' ? true : ev.date === selectedDate)
            .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
        , [events, selectedDate, activeView])

    function prevMonth() { setViewDate(v => v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }) }
    function nextMonth() { setViewDate(v => v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }) }
    function goToday() {
        const t = new Date()
        setViewDate({ y: t.getFullYear(), m: t.getMonth() })
        const s = fmt(t); setSelectedDate(s); setForm(f => ({ ...f, date: s }))
    }
    function selectDay(cell) {
        const ds = `${cell.year}-${String(cell.month + 1).padStart(2, '0')}-${String(cell.day).padStart(2, '0')}`
        setSelectedDate(ds); setForm(f => ({ ...f, date: ds }))
    }
    function addEvent() {
        if (!form.name.trim() || !form.date) { setFormErr(true); setTimeout(() => setFormErr(false), 1500); return }
        setEvents(evs => [...evs, { ...form, id: nextId }])
        setNextId(n => n + 1); setSelectedDate(form.date)
        setForm(f => ({ ...f, name: '', time: '', note: '' }))
        setShowAddForm(false) // Modal band karo event add thay pachi
    }
    function deleteEvent(id) { setEvents(evs => evs.filter(e => e.id !== id)) }

    const dayLabels = isMobile ? DAYS_SHORT : DAYS_FULL

    return (
        <div>
            {/* ── Top bar ── */}
            <div className="cp-top">
                <div>
                    <div style={{ fontSize: '1.15rem', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--text1)' }}>Calendar</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                        {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div className="cp-vtabs">
                        {['month', 'week', 'agenda'].map(v => (
                            <button key={v} className={`cp-vtab${activeView === v ? ' active' : ''}`} onClick={() => setActiveView(v)}>
                                {v.charAt(0).toUpperCase() + v.slice(1)}
                            </button>
                        ))}
                    </div>
                    {!isMobile && selectedDate && (
                        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-full)', padding: '3px 10px' }}>
                            {fmtDisplay(selectedDate)}
                        </span>
                    )}
                </div>
            </div>

            {/* ── Main grid ── */}
            <div className="cp-wrap">

                {/* Calendar */}
                <div className="cp-cal-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                        <span style={{ fontSize: '0.95rem', fontWeight: 700, letterSpacing: '-0.02em' }}>{MONTHS[viewDate.m]} {viewDate.y}</span>
                        <div style={{ display: 'flex', gap: 6 }}>
                            {[
                                { fn: prevMonth, content: <ChevronLeft size={14} /> },
                                { fn: goToday, content: <span style={{ fontSize: 11, fontWeight: 600 }}>Today</span> },
                                { fn: nextMonth, content: <ChevronRight size={14} /> },
                            ].map((b, i) => (
                                <button key={i} onClick={b.fn} style={{
                                    background: 'var(--bg-raised)', border: '1px solid var(--border)', color: 'var(--text2)',
                                    borderRadius: 'var(--radius-sm)', height: 30, minWidth: 30, padding: '0 8px',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s',
                                }}
                                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-active)'; e.currentTarget.style.color = 'var(--text1)' }}
                                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)' }}
                                >{b.content}</button>
                            ))}
                        </div>
                    </div>

                    <div style={{ padding: '10px 12px 14px' }}>
                        <div className="cp-dow">
                            {dayLabels.map((d, i) => (
                                <div key={i} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '4px 0' }}>{d}</div>
                            ))}
                        </div>
                        <div className="cp-days">
                            {calCells.map((cell, i) => {
                                const ds = `${cell.year}-${String(cell.month + 1).padStart(2, '0')}-${String(cell.day).padStart(2, '0')}`
                                const isToday = ds === todayStr
                                const isSel = ds === selectedDate
                                const dayEvs = evsByDate[ds] || []
                                return (
                                    <div key={i} onClick={() => selectDay(cell)}
                                        className={`cp-day${isToday ? ' today' : ''}${!cell.cur ? ' other' : ''}${isSel ? ' sel' : ''}`}
                                    >
                                        <div className="cp-day-num">{cell.day}</div>
                                        {dayEvs.length > 0 && (
                                            <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', marginTop: 2 }}>
                                                {dayEvs.slice(0, 3).map((ev, j) => (
                                                    <div key={j} style={{ width: 5, height: 5, borderRadius: '50%', background: ev.color, flexShrink: 0 }} />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {isMobile && selectedDate && (
                        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)' }}>{fmtDisplay(selectedDate)}</span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', background: 'rgba(79,70,229,0.10)', border: '1px solid var(--border)', borderRadius: 'var(--radius-full)', padding: '2px 9px' }}>
                                {(evsByDate[selectedDate] || []).length} event{(evsByDate[selectedDate] || []).length !== 1 ? 's' : ''}
                            </span>
                        </div>
                    )}
                </div>

                {/* Desktop right panel */}
                <div className="cp-right cp-right-desktop">
                    <div className="cp-form-card">
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text1)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CalendarPlus size={15} color="var(--accent)" /> Schedule Event
                        </div>
                        <AddEventForm form={form} setForm={setForm} formErr={formErr} onAdd={addEvent} />
                    </div>
                    <EventsList events={filteredEvents} onDelete={deleteEvent} activeView={activeView} selectedDate={selectedDate} />
                </div>

                {/* Mobile stacked sections */}
                <div className="cp-mob-section">
                    <EventsList events={filteredEvents} onDelete={deleteEvent} activeView={activeView} selectedDate={selectedDate} />

                    {/* ── Mobile: Button opens MODAL instead of inline ── */}
                    <button className="cp-mob-add-toggle" onClick={() => setShowAddForm(true)}>
                        <Plus size={15} />
                        Schedule New Event
                    </button>
                </div>

            </div>

            {/* ── Mobile Bottom Sheet Modal ── */}
            {isMobile && showAddForm && (
                <MobileAddModal
                    form={form}
                    setForm={setForm}
                    formErr={formErr}
                    onAdd={addEvent}
                    onClose={() => setShowAddForm(false)}
                />
            )}
        </div>
    )
}